import React, { createContext, useState, useCallback, useEffect, useMemo } from 'react';
import api from '../services/api';
import { storage } from '../utils/storage';

export const AuthContext = createContext(null);

export class MfaRequired extends Error {
  constructor(mfaToken, maskedEmail, totpMode = false) {
    super('MFA_REQUIRED');
    this.name = 'MfaRequired';
    this.mfaToken = mfaToken;
    this.maskedEmail = maskedEmail;
    this.totpMode = totpMode;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => storage.getJSON('user'));
  const [token, setToken] = useState(() => storage.get('token'));

  // activeOrg: topbar'da seçili org context'i (null = "tüm org'larım" modu;
  // platform admin için null = tüm platform). Backend her isteğe
  // X-Active-Org header'ı olarak gönderilir (services/api.js).
  const [activeOrg, setActiveOrgState] = useState(() => storage.getJSON('active_org'));
  const [orgs, setOrgs] = useState([]);

  // /me sonrası org/üyelik durumunu uygula:
  // - platform admin: org listesi ayrıca /api/organizations'dan gelir
  // - tek org'lu üye: aktif org otomatik o org (eski davranışla birebir aynı)
  // - çok org'lu üye: seçim yoksa "tüm org'larım" modu; kayıtlı seçim artık
  //   üyelikler arasında değilse temizlenir
  const applyMe = useCallback((data) => {
    storage.setJSON('user', data);
    setUser(data);
    if (data.role === 'admin') return;
    const myOrgs = data.orgs || [];
    setOrgs(myOrgs);
    if (myOrgs.length === 1) {
      setActiveOrgState(myOrgs[0]);
      storage.setJSON('active_org', myOrgs[0]);
    } else {
      const stored = storage.getJSON('active_org');
      if (stored && !myOrgs.some(o => o.id === stored.id)) {
        setActiveOrgState(null);
        storage.remove('active_org');
      }
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api.get('/api/auth/me').then(({ data }) => {
      if (!cancelled) applyMe(data);
    }).catch(() => {});
    // Org selection changes the effective permissions. Also discard in-flight
    // responses after switching scope or signing out.
    return () => { cancelled = true; };
  }, [applyMe, token, activeOrg?.id]);

  // platform admin için org listesini çek
  useEffect(() => {
    if (!token || user?.role !== 'admin') return;
    api.get('/api/organizations').then(({ data }) => {
      setOrgs(data.organizations || []);
    }).catch(() => {});
  }, [token, user]);

  // activeOrg localStorage'dan bir snapshot olarak yükleniyor (login sonrası
  // hiç tazelenmiyordu) — org listesi (orgs) her fetch'te güncel geliyor,
  // seçili org'u o listedeki güncel kayıtla senkron tut (kredi bakiyesi gibi
  // alanlar org listesi tazelenince otomatik yansısın).
  useEffect(() => {
    if (!activeOrg || orgs.length === 0) return;
    const fresh = orgs.find(o => o.id === activeOrg.id);
    if (fresh && JSON.stringify(fresh) !== JSON.stringify(activeOrg)) {
      setActiveOrgState(fresh);
      storage.setJSON('active_org', fresh);
    }
  }, [orgs, activeOrg]);

  const setActiveOrg = useCallback((org) => {
    setActiveOrgState(org);
    if (org) {
      storage.setJSON('active_org', org);
    } else {
      storage.remove('active_org');
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/api/auth/login', { email, password });
    if (data.mfa_required) {
      throw new MfaRequired(data.mfa_token, data.masked_email, !!data.totp_mode);
    }
    storage.set('token', data.token);
    setToken(data.token);
    const me = await api.get('/api/auth/me', {
      headers: { Authorization: `Bearer ${data.token}` },
    });
    applyMe(me.data);
    return me.data;
  }, [applyMe]);

  const verifyMfa = useCallback(async (mfaToken, code) => {
    const { data } = await api.post('/api/auth/verify-mfa', { mfa_token: mfaToken, code });
    storage.set('token', data.token);
    setToken(data.token);
    const me = await api.get('/api/auth/me', {
      headers: { Authorization: `Bearer ${data.token}` },
    });
    applyMe(me.data);
    return me.data;
  }, [applyMe]);

  const logout = useCallback(() => {
    // Capture the credential before clearing storage. Axios dispatches its
    // interceptors asynchronously, so relying on storage there loses it.
    const currentToken = storage.get('token');
    const revocation = currentToken
      ? api.post('/api/auth/logout', null, {
        headers: { Authorization: `Bearer ${currentToken}` },
        timeout: 10000,
      }).catch(() => { /* Local sign-out must still complete if offline. */ })
      : Promise.resolve();
    storage.remove('token');
    storage.remove('user');
    storage.remove('active_org');
    try { sessionStorage.removeItem('ai_assistant_chat'); } catch { /* noop */ }
    setToken(null);
    setUser(null);
    setActiveOrgState(null);
    setOrgs([]);
    return revocation;
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/api/auth/me');
      applyMe(data);
    } catch {}
  }, [applyMe]);

  const refreshOrgs = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await api.get('/api/organizations');
      setOrgs(data.organizations || []);
    } catch {}
  }, [token]);

  const value = useMemo(() => ({
    user, token, login, verifyMfa, logout, refreshUser,
    isAuthenticated: !!token,
    activeOrg, setActiveOrg,
    orgs, refreshOrgs,
  }), [user, token, login, verifyMfa, logout, refreshUser, activeOrg, setActiveOrg, orgs, refreshOrgs]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
